import { isAxiosError } from 'axios';
import { apiClient } from './api';
import type {
  Video,
  VideoApiResponse,
  VideoUploadResponse,
  PoseExtractionResponse,
  VideoPersonsResponse,
  RawPoseFrame,
} from '@/types/api';

/**
 * Transform backend video response to frontend format
 */
function transformVideoResponse(apiVideo: VideoApiResponse): Video {
  return {
    id: apiVideo.video_id,
    filename: apiVideo.filename,
    file_path: undefined, // Backend doesn't expose this
    uploaded_at: undefined, // Backend doesn't return this yet
    duration_seconds: apiVideo.duration,
    duration: apiVideo.duration,
    frame_count: apiVideo.frame_count,
    fps: apiVideo.fps,
    width: apiVideo.width,
    height: apiVideo.height,
    file_size: apiVideo.file_size,
  };
}

/**
 * Upload a video file
 *
 * @param videoType - 'athlete' (ephemeral, triggers pre-extraction) or 'template' (permanent storage).
 *   Sent as a query parameter because the backend reads it via Query(), not as a form field.
 *   Defaults to 'athlete' — the safer default since permanent storage is harder to recover from
 *   than ephemeral.
 */
export async function uploadVideo(
  file: File,
  videoType: 'template' | 'athlete' = 'athlete',
  onProgress?: (progress: number) => void
): Promise<VideoUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<VideoUploadResponse>(
    `/videos/upload?video_type=${videoType}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    }
  );

  return response.data;
}

/**
 * Get all videos
 */
export async function getVideos(): Promise<Video[]> {
  const response = await apiClient.get<VideoApiResponse[]>('/videos');
  return response.data.map(transformVideoResponse);
}

/**
 * Get a single video by ID
 */
export async function getVideo(videoId: string): Promise<Video> {
  const response = await apiClient.get<VideoApiResponse>(`/videos/${videoId}`);
  return transformVideoResponse(response.data);
}

/**
 * Delete a video
 */
export async function deleteVideo(videoId: string): Promise<void> {
  await apiClient.delete(`/videos/${videoId}`);
}

/**
 * Get existing pose data from a video
 */
export async function getPoseData(videoId: string): Promise<RawPoseFrame[]> {
  const response = await apiClient.get<RawPoseFrame[]>(`/videos/${videoId}/pose`);
  return response.data;
}

/**
 * Get pose data including 3D lifted landmarks when available.
 * Returns frames (2D), landmarks_3d (H36M 17-joint format), and metadata
 * about the lifting model used.
 */
export async function getPoseData3D(videoId: string): Promise<{
  frames: RawPoseFrame[];
  landmarks_3d: number[][][] | null;
  lifting_model: string | null;
  is_gravity_aligned: boolean | null;
}> {
  const response = await apiClient.get(`/videos/${videoId}/pose`, {
    params: { include_3d: 'true' },
  });
  return response.data;
}

/**
 * Extract pose data from a video
 */
export async function extractPoseData(
  videoId: string
): Promise<PoseExtractionResponse> {
  const response = await apiClient.post<PoseExtractionResponse>(
    `/videos/${videoId}/pose`,
    {}
  );
  return response.data;
}

/**
 * Fetch pose data for a video, automatically retrying on 202 Accepted.
 *
 * The backend returns 202 + Retry-After header when pose extraction is
 * scheduled but not yet complete. This function polls until a 200 response
 * arrives or maxAttempts is exhausted.
 */
export async function getPoseDataWithRetry(
  videoId: string,
  maxAttempts: number = 30,
): Promise<RawPoseFrame[]> {
  let transportFailures = 0;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await apiClient.get<RawPoseFrame[]>(`/videos/${videoId}/pose`);
      // BUG FIX (2026-07-29): axios RESOLVES 2xx including 202, so the
      // catch-branch below never saw the 202 — the {status:"processing"}
      // body was returned as pose data (→ silently empty overlays, and a
      // page-crashing TypeError once consumers iterated it). Handle the
      // still-processing case on the RESOLVED path and only ever return
      // an actual frames array.
      if (response.status === 202 || !Array.isArray(response.data)) {
        const retryAfterResolved = parseInt(
          (response.headers as Record<string, string>)?.['retry-after'] ?? '10',
          10,
        ) || 10;
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, retryAfterResolved * 1000));
          continue;
        }
        throw new Error(`Pose extraction did not complete after ${maxAttempts} attempts`);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 202) {
        const retryAfter = parseInt(
          (error.response.headers as Record<string, string>)?.['retry-after'] ?? '10',
          10,
        ) || 10;
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, retryAfter * 1000));
          continue;
        }
        // Final attempt also returned 202 — pose extraction still not complete.
        throw new Error(`Pose extraction did not complete after ${maxAttempts} attempts`);
      }
      // TRANSPORT-level failure (no HTTP response at all): retry with a short
      // backoff instead of giving up. Prod incident 2026-08-20 (self-eval
      // 9440d89c): Cloudflare HTTP/3 dropped BOTH ~1-2MB pose payloads with
      // net::ERR_QUIC_PROTOCOL_ERROR while every small API call succeeded.
      // This function then threw on the first blip, and the caller's
      // useQuery({retry: false}) made the failure PERMANENT for the session —
      // every overlay (pose, traces, ghost, regions) silently blank over one
      // transient network hiccup. Cap transport retries well below the 202
      // budget: 3 quick attempts is enough to ride out a connection fallback
      // (H3 -> H2) without stalling the page for minutes.
      if (isAxiosError(error) && !error.response) {
        transportFailures += 1;
        if (transportFailures <= 3 && attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 1000 * transportFailures));
          continue;
        }
      }
      throw error;
    }
  }
  throw new Error(`Pose extraction did not complete after ${maxAttempts} attempts`);
}

/**
 * Fetch anthropometric display-quality 3D pose data, automatically retrying on 202 Accepted.
 *
 * Returns null gracefully when the server indicates no 3D data is available
 * after maxAttempts, rather than throwing — callers treat 3D as optional.
 */
export async function getDisplayPose3DWithRetry(
  videoId: string,
  maxAttempts: number = 30,
): Promise<{
  joints_3d: number[][][];
  source: string;
  cached: boolean;
  pose_3d_depth_reliable?: boolean | null;
} | null> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const { data } = await apiClient.get(`/videos/${videoId}/display-3d`);
      return data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 202) {
        const retryAfter = parseInt(
          (error.response.headers as Record<string, string>)?.['retry-after'] ?? '10',
          10,
        ) || 10;
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, retryAfter * 1000));
          continue;
        }
        // Exhausted retries on 202 — return null gracefully (3D is optional)
        return null;
      }
      // Non-202 errors: return null for non-critical 3D data
      return null;
    }
  }
  return null;
}

/**
 * Get video URL for playback
 *
 * The backend's get_video_path function will automatically try common extensions
 * if the exact filename is not found, so we can pass video IDs with or without extensions.
 *
 * @deprecated The `/videos/{id}/stream` endpoint now requires either a bearer
 * token or a signed `sig`/`exp` query pair (media-auth security sprint,
 * 2026-09-21 — see MEDIA_AUTH_CONTRACT.md). `<video src>` / `<img src>` never
 * send the bearer header, so a URL built by this function will 401. Use
 * `useMediaUrl(videoId)` (frontend/src/hooks/useMediaUrl.ts) instead, which
 * fetches a short-lived signed token and builds the URL via
 * `buildSignedStreamUrl`. Kept exported only for callers not yet migrated.
 */
export function getVideoUrl(videoId: string): string {
  if (!videoId) {
    console.error('getVideoUrl: videoId is undefined or empty');
    return '';
  }

  // Pass the video ID as-is - backend will handle extension resolution
  return `${apiClient.defaults.baseURL}/videos/${videoId}/stream`;
}

// ---------------------------------------------------------------------------
// Signed media URLs (media-auth security sprint, 2026-09-21)
//
// `/videos/{id}/stream` and `/videos/{id}/thumbnail` no longer allow anonymous
// access (backend/app/api/annotation_routes.py). Since <video src> / <img src>
// never carry the bearer header, the backend accepts a short-lived HMAC
// signature (`sig` + `exp` query params) minted via `/videos/{id}/media-token`
// as an alternative to the bearer header. See MEDIA_AUTH_CONTRACT.md (the
// single source of truth shared with the backend) for the full contract.
// ---------------------------------------------------------------------------

export interface MediaTokenResponse {
  media_id: string;
  /**
   * 'calibration_take' added for Example Performances (backlog/example-
   * performances-template-resources.md, PR 1+2) — a dedicated HMAC namespace
   * for streaming a coach-exposed calibration take, isolated from the
   * generic 'video' namespace (see MEDIA_AUTH_CONTRACT.md).
   */
  kind: 'video' | 'teaching' | 'calibration_take';
  sig: string;
  exp: number;
  /** Seconds of validity remaining from mint time — used to schedule refresh. */
  expires_in: number;
}

/**
 * Mint a short-lived signed-URL token for a video (kind="video").
 * Requires the bearer header — this call itself goes through apiClient, not
 * a bare <video>/<img> tag, so the header is present as usual.
 */
export async function fetchMediaToken(videoId: string): Promise<MediaTokenResponse> {
  const response = await apiClient.get<MediaTokenResponse>(`/videos/${videoId}/media-token`);
  return response.data;
}

/**
 * Build the signed streaming URL for a video from a previously-minted token.
 */
export function buildSignedStreamUrl(videoId: string, token: MediaTokenResponse): string {
  return `${apiClient.defaults.baseURL}/videos/${videoId}/stream?sig=${token.sig}&exp=${token.exp}`;
}

/**
 * Build the signed thumbnail URL for a video from a previously-minted token.
 * Keeps the existing `frame`/`width` query params exactly as `getThumbnailUrl`
 * did, appending `sig`/`exp` alongside them.
 */
export function buildSignedThumbnailUrl(
  videoId: string,
  token: MediaTokenResponse,
  frame?: number,
  width?: number
): string {
  const params = new URLSearchParams();
  if (frame !== undefined && frame > 0) {
    params.set('frame', frame.toString());
  }
  if (width !== undefined) {
    params.set('width', width.toString());
  }
  params.set('sig', token.sig);
  params.set('exp', token.exp.toString());

  return `${apiClient.defaults.baseURL}/videos/${videoId}/thumbnail?${params.toString()}`;
}

/**
 * Get detected persons in a video frame (for multi-person selection)
 *
 * Returns bounding boxes and optional crop thumbnails for each tracked person
 * found in a representative sample frame. When person_count === 1 the caller
 * should skip showing a picker and auto-select person 0.
 */
export async function getVideoPersons(videoId: string): Promise<VideoPersonsResponse> {
  const response = await apiClient.get<VideoPersonsResponse>(`/videos/${videoId}/persons`);
  return response.data;
}

/**
 * Get anthropometric display-quality 3D pose data for a video.
 *
 * Calls the /display-3d endpoint which returns H36M 17-joint data that has
 * been post-processed with anthropometric bone-length constraints — better
 * suited for 3D viewer rendering than the raw VideoPose3D output used for scoring.
 *
 * Returns null when the endpoint returns a non-2xx status (e.g. no pose cache).
 */
export async function getDisplayPose3D(videoId: string): Promise<{
  joints_3d: number[][][];  // (T, 17, 3) — Y-up, hip-centred, metres
  source: string;
  cached: boolean;
  /** True when the template camera view reliably captures depth (frontal/diagonal).
   *  False for side-view footage where monocular depth is unreliable.
   *  Null when the camera view is unknown (no linked template found). */
  pose_3d_depth_reliable?: boolean | null;
} | null> {
  try {
    const response = await apiClient.get(`/videos/${videoId}/display-3d`);
    return response.data;
  } catch {
    return null;
  }
}

/**
 * Get thumbnail URL for a video
 *
 * @param videoId - Video identifier (filename)
 * @param frame - Optional frame number to extract (default: 0)
 * @param width - Optional max width in pixels (default: 320)
 * @returns Full URL to the thumbnail endpoint
 *
 * @deprecated Same auth gap as `getVideoUrl` — `/videos/{id}/thumbnail` now
 * requires a bearer token or a signed `sig`/`exp` pair. Use
 * `useMediaUrl(videoId).thumbnailUrl(frame, width)` instead. Kept exported
 * only for callers not yet migrated.
 */
export function getThumbnailUrl(videoId: string, frame?: number, width?: number): string {
  if (!videoId) {
    console.error('getThumbnailUrl: videoId is undefined or empty');
    return '';
  }

  const params = new URLSearchParams();
  if (frame !== undefined && frame > 0) {
    params.set('frame', frame.toString());
  }
  if (width !== undefined) {
    params.set('width', width.toString());
  }

  const queryString = params.toString();
  return `${apiClient.defaults.baseURL}/videos/${videoId}/thumbnail${queryString ? `?${queryString}` : ''}`;
}
